import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AACChromeAPI from '@amzn/aac-chrome-api';
import { selectEmbeddedChatOpen, setEmbeddedChatOpen } from 'src/app/slice';
import {
  initA3CEmbeddedConfig,
  createEmbeddedChatContainer,
  setEmbeddedChatVisible,
  setCurrentFocusMode,
  handleEmbeddedChatResize,
  DEFAULT_PANEL_WIDTH,
} from 'src/commons/utils/a3cEmbeddedChat';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';

interface EmbeddedChatManagerProps {
  isFocusMode: boolean;
}

/**
 * Pure side-effect component that manages embedded chat lifecycle:
 * - Registers the bidirectional toggle callback on ChromeConfiguration
 * - Initializes A3CConfig and creates the chat container
 * - Syncs chat visibility with Redux state
 *
 * Renders nothing. Only mounted when both ADS_AGENT_P0 and
 * ADS_AGENT_EMBEDDED weblabs are enabled.
 */
export const EmbeddedChatManager: React.VFC<EmbeddedChatManagerProps> = ({
  isFocusMode,
}) => {
  const dispatch = useDispatch();
  const isEmbeddedChatOpen = useSelector(selectEmbeddedChatOpen);
  const chromeConfig = useChromeConfigContext();
  const selectedAgentIdentifier =
    chromeConfig?.a3cConfig?.embeddedChatConfig?.selectedAgentIdentifier;
  const defaultWidth =
    chromeConfig?.a3cConfig?.embeddedChatConfig?.panelSizes?.defaultWidth ??
    DEFAULT_PANEL_WIDTH;

  // Register the bidirectional toggle and resize callbacks on ChromeConfiguration
  // so external callers can open/close and resize the embedded chat panel.
  useEffect(() => {
    const chromeConfiguration = AACChromeAPI.getChromeConfiguration();
    chromeConfiguration.setOnEmbeddedChatToggle((isOpen: boolean) => {
      setEmbeddedChatVisible(
        isOpen && !!selectedAgentIdentifier,
        defaultWidth,
        isFocusMode,
      );
      dispatch(setEmbeddedChatOpen(isOpen));
    });
    chromeConfiguration.setOnEmbeddedChatResize((width: number) => {
      handleEmbeddedChatResize(width, isFocusMode);
    });
    AACChromeAPI.setChromeConfiguration(chromeConfiguration);
  }, [defaultWidth, isFocusMode, dispatch, selectedAgentIdentifier]);

  // Initialize A3CConfig and create the fixed-position container.
  useEffect(() => {
    setCurrentFocusMode(isFocusMode);
    initA3CEmbeddedConfig({
      closable: !isFocusMode,
      onStateChange: (isOpen) => {
        dispatch(setEmbeddedChatOpen(isOpen));
      },
      selectedAgentIdentifier,
    });
    createEmbeddedChatContainer(isFocusMode);

    // Sync closable state to ChromeConfiguration
    if (selectedAgentIdentifier) {
      const chromeConfiguration = AACChromeAPI.getChromeConfiguration();
      chromeConfiguration.setEmbeddedChatClosable(!isFocusMode);
      AACChromeAPI.setChromeConfiguration(chromeConfiguration);
    }
  }, [isFocusMode, selectedAgentIdentifier, dispatch]);

  // Sync chat panel visibility with Redux state.
  useEffect(() => {
    setEmbeddedChatVisible(
      !!selectedAgentIdentifier && isEmbeddedChatOpen,
      defaultWidth,
      isFocusMode,
    );
  }, [isEmbeddedChatOpen, selectedAgentIdentifier, isFocusMode, defaultWidth]);

  return null;
};
